import React, { useState, useEffect } from "react";
import { contactsAPI } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Users,
  Building2,
  Briefcase,
  UserCircle,
  Building,
  PlusCircle,
  Search,
  Trash2,
  Edit,
  FileText,
  Loader2,
} from "lucide-react";

const CATEGORIES = [
  { value: "clienti", label: "Clienti", icon: Users, color: "bg-blue-100 text-blue-700" },
  { value: "fornitori", label: "Fornitori", icon: Building2, color: "bg-emerald-100 text-emerald-700" },
  { value: "dipendenti", label: "Dipendenti", icon: Briefcase, color: "bg-amber-100 text-amber-700" },
  { value: "altri_enti", label: "Altri Enti", icon: Building, color: "bg-purple-100 text-purple-700" },
];

export function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    category: "clienti",
    email: "",
    phone: "",
    notes: "",
  });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    loadContacts();
  }, [search, categoryFilter]);

  const loadContacts = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      const response = await contactsAPI.getAll(params);
      setContacts(response.data);
    } catch (error) {
      showMessage("Errore nel caricamento dei contatti", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      showMessage("Il nome è obbligatorio", "error");
      return;
    }

    setSaving(true);
    try {
      if (editContact) {
        await contactsAPI.update(editContact.id, formData);
        showMessage("Contatto aggiornato");
      } else {
        await contactsAPI.create(formData);
        showMessage("Contatto creato");
      }
      setShowForm(false);
      setEditContact(null);
      setFormData({ name: "", category: "clienti", email: "", phone: "", notes: "" });
      loadContacts();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (contact) => {
    setEditContact(contact);
    setFormData({
      name: contact.name,
      category: contact.category,
      email: contact.email || "",
      phone: contact.phone || "",
      notes: contact.notes || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Eliminare il contatto "${contact.name}"?`)) return;
    try {
      await contactsAPI.delete(contact.id);
      showMessage("Contatto eliminato");
      loadContacts();
    } catch (error) {
      showMessage("Errore durante l'eliminazione", "error");
    }
  };

  const viewProtocols = (contact) => {
    navigate(`/settings/contacts/${contact.id}/protocols`);
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[3];
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="contacts-page">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" ? "bg-red-500 text-white" : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <UserCircle size={32} className="text-[#2D7BB7]" />
            Anagrafica Contatti
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci clienti, fornitori, dipendenti e altri enti
          </p>
        </div>
        <Button
          onClick={() => {
            setEditContact(null);
            setFormData({ name: "", category: "clienti", email: "", phone: "", notes: "" });
            setShowForm(true);
          }}
          className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
          data-testid="add-contact-btn"
        >
          <PlusCircle className="mr-2" size={18} />
          Nuovo Contatto
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Cerca per nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background border-input"
                data-testid="search-contacts"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={categoryFilter === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoryFilter("")}
                className={categoryFilter === "" ? "bg-[#2D7BB7]" : ""}
              >
                Tutti
              </Button>
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat.value}
                  variant={categoryFilter === cat.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategoryFilter(cat.value)}
                  className={categoryFilter === cat.value ? "bg-[#2D7BB7]" : ""}
                >
                  <cat.icon size={14} className="mr-1" />
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editContact ? "Modifica Contatto" : "Nuovo Contatto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome completo"
                data-testid="contact-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select
                value={formData.category}
                onValueChange={(val) => setFormData({ ...formData, category: val })}
              >
                <SelectTrigger data-testid="contact-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@esempio.it"
              />
            </div>

            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+39 000 000 0000"
              />
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editContact ? "Salva" : "Crea"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contacts List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground">
            {contacts.length} contatti
          </CardTitle>
        </CardHeader>
        <CardContent>
          {contacts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun contatto trovato</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map((contact) => {
                const catInfo = getCategoryInfo(contact.category);
                const CatIcon = catInfo.icon;
                return (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                    data-testid={`contact-item-${contact.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${catInfo.color}`}>
                        <CatIcon size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{contact.name}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <Badge variant="outline" className={catInfo.color}>
                            {catInfo.label}
                          </Badge>
                          {contact.email && <span>{contact.email}</span>}
                          {contact.phone && <span>{contact.phone}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewProtocols(contact)}
                        className="border-border"
                        data-testid={`view-protocols-${contact.id}`}
                      >
                        <FileText size={16} className="mr-1" />
                        {contact.protocol_count} protocolli
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(contact)}
                        className="border-border"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(contact)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
